import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing draggable question layout with local storage
 * @param {string} soNumber - SO number for storage key
 * @param {string} sessionId - Session ID for storage key
 * @param {Array} questions - Array of question objects
 * @returns {Object} Layout management functions and state
 */
export function useQuestionLayout(soNumber, sessionId, questions) {
  const [customizeMode, setCustomizeMode] = useState(false);
  const [layout, setLayout] = useState(null);
  
  const storageKey = `question-layout-${soNumber}-session-${sessionId}`;
  
  // Default sections with custom column layout
  const getDefaultLayout = useCallback(() => {
    if (!questions || questions.length === 0) return {
      sections: {},
      sectionOrder: [],
      sectionColumns: {}
    };
    
    const sections = {};
    const sectionOrder = [];
    const sectionColumns = {};
    let questionIndex = 0;
    
    // First section: 2 questions, 2 columns
    if (questions.length >= 2) {
      const sectionName = 'section-1';
      sections[sectionName] = questions.slice(0, 2);
      sectionOrder.push(sectionName);
      sectionColumns[sectionName] = 2;
      questionIndex = 2;
    } else if (questions.length === 1) {
      const sectionName = 'section-1';
      sections[sectionName] = [questions[0]];
      sectionOrder.push(sectionName);
      sectionColumns[sectionName] = 1;
      questionIndex = 1;
    }
    
    // Second section: 3 questions, 3 columns
    if (questions.length >= questionIndex + 3) {
      const sectionName = 'section-2';
      sections[sectionName] = questions.slice(questionIndex, questionIndex + 3);
      sectionOrder.push(sectionName);
      sectionColumns[sectionName] = 3;
      questionIndex += 3;
    } else if (questions.length > questionIndex) {
      const sectionName = 'section-2';
      const remainingQuestions = questions.slice(questionIndex);
      sections[sectionName] = remainingQuestions;
      sectionOrder.push(sectionName);
      sectionColumns[sectionName] = remainingQuestions.length;
      questionIndex = questions.length;
    }
    
    // Remaining questions: one per section, single column
    let sectionCounter = 3;
    while (questionIndex < questions.length) {
      const sectionName = `section-${sectionCounter}`;
      sections[sectionName] = [questions[questionIndex]];
      sectionOrder.push(sectionName);
      sectionColumns[sectionName] = 1;
      questionIndex++;
      sectionCounter++;
    }
    
    return {
      sections,
      sectionOrder,
      sectionColumns
    };
  }, [questions]);
  
  // Load layout from localStorage or use default
  useEffect(() => {
    if (!questions || questions.length === 0) return;
    
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Validate parsed layout structure
        if (!parsed || typeof parsed !== 'object' ||
            !parsed.sections || typeof parsed.sections !== 'object' ||
            !parsed.sectionOrder || !Array.isArray(parsed.sectionOrder)) {
          console.warn('Invalid saved layout structure, using default');
          setLayout(getDefaultLayout());
          return;
        }
        
        // Validate that sections contain arrays of question objects
        const sectionsValid = Object.values(parsed.sections).every(section => 
          Array.isArray(section) && section.every(q => q && typeof q === 'object' && q.fieldId)
        );
        
        if (!sectionsValid) {
          console.warn('Invalid sections structure, using default');
          setLayout(getDefaultLayout());
          return;
        }
        
        // Validate that all questions are still present
        const allQuestionIds = questions.map(q => q.fieldId);
        const savedQuestionIds = Object.values(parsed.sections).flat().map(q => q.fieldId);
        const hasAllQuestions = allQuestionIds.every(id => savedQuestionIds.includes(id));
        
        if (hasAllQuestions) {
          // Ensure sectionColumns exists (for backward compatibility)
          if (!parsed.sectionColumns) {
            parsed.sectionColumns = Object.keys(parsed.sections).reduce((acc, key) => {
              acc[key] = 1; // Default to 1 column
              return acc;
            }, {});
          }
          setLayout(parsed);
          return;
        }
      }
    } catch (error) {
      console.warn('Failed to load saved layout:', error);
    }
    
    // Use default layout
    setLayout(getDefaultLayout());
  }, [questions, storageKey, getDefaultLayout]);
  
  // Save layout to localStorage
  const saveLayout = useCallback((newLayout) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(newLayout));
      setLayout(newLayout);
    } catch (error) {
      console.error('Failed to save layout:', error);
    }
  }, [storageKey]);
  
  // Reset to default layout
  const resetLayout = useCallback(() => {
    const defaultLayout = getDefaultLayout();
    localStorage.removeItem(storageKey);
    setLayout(defaultLayout);
  }, [getDefaultLayout, storageKey]);
  
  // Move question between sections
  const moveQuestion = useCallback((questionId, fromSection, toSection, toIndex) => {
    if (!layout) return;
    
    const newLayout = {
      ...layout,
      sections: { ...layout.sections },
      sectionOrder: [...layout.sectionOrder],
      sectionColumns: { ...layout.sectionColumns },
    };
    const newSections = newLayout.sections;
    
    // Remove question from source section
    newSections[fromSection] = [...newSections[fromSection].filter(q => q.fieldId !== questionId)];
    
    // Find the question object
    const question = questions.find(q => q.fieldId === questionId);
    if (!question) return;
    
    // Add to destination section
    if (!newSections[toSection]) {
      newSections[toSection] = [];
      // Add new section to sectionOrder if it doesn't exist
      if (!newLayout.sectionOrder.includes(toSection)) {
        newLayout.sectionOrder = [...newLayout.sectionOrder, toSection];
      }
      // Add default column count for new section
      if (!newLayout.sectionColumns[toSection]) {
        newLayout.sectionColumns[toSection] = 1;
      }
    }
    const safeIndex = Math.max(0, Math.min(toIndex, newSections[toSection].length));
    const newDestinationSection = [...newSections[toSection]];
    newDestinationSection.splice(safeIndex, 0, question);
    newSections[toSection] = newDestinationSection;
    
    // Clean up empty sections
    Object.keys(newSections).forEach(key => {
      if (newSections[key].length === 0) {
        delete newSections[key];
        delete newLayout.sectionColumns[key];
        newLayout.sectionOrder = newLayout.sectionOrder.filter(s => s !== key);
      }
    });
    
    newLayout.sections = newSections;
    setLayout(newLayout);
  }, [layout, questions]);
  
  // Reorder sections
  const reorderSections = useCallback((newSectionOrder) => {
    if (!layout) return;
    
    const newLayout = {
      ...layout,
      sectionOrder: newSectionOrder
    };
    setLayout(newLayout);
  }, [layout]);
  
  // Add new section
  const addSection = useCallback((sectionName) => {
    if (!layout || layout.sections[sectionName]) return;
    
    const newLayout = {
      ...layout,
      sections: {
        ...layout.sections,
        [sectionName]: []
      },
      sectionOrder: [...layout.sectionOrder, sectionName],
      sectionColumns: {
        ...layout.sectionColumns,
        [sectionName]: 1
      }
    };
    setLayout(newLayout);
  }, [layout]);
  
  // Rename section
  const renameSection = useCallback((oldName, newName) => {
    if (!layout || !layout.sections[oldName] || layout.sections[newName]) return;
    
    const newSections = { ...layout.sections };
    newSections[newName] = newSections[oldName];
    delete newSections[oldName];
    
    const newSectionColumns = { ...layout.sectionColumns };
    newSectionColumns[newName] = newSectionColumns[oldName] || 1;
    delete newSectionColumns[oldName];
    
    const newLayout = {
      sections: newSections,
      sectionOrder: layout.sectionOrder.map(s => s === oldName ? newName : s),
      sectionColumns: newSectionColumns
    };
    setLayout(newLayout);
  }, [layout]);
  
  // Set section column count
  const setSectionColumns = useCallback((sectionName, columnCount) => {
    if (!layout || !layout.sections[sectionName]) return;
    
    const newLayout = {
      ...layout,
      sectionColumns: {
        ...layout.sectionColumns,
        [sectionName]: Math.max(1, Math.min(3, columnCount)) // Clamp between 1-3
      }
    };
    setLayout(newLayout);
  }, [layout]);
  
  return {
    layout,
    customizeMode,
    setCustomizeMode,
    saveLayout: () => saveLayout(layout),
    resetLayout,
    moveQuestion,
    reorderSections,
    addSection,
    renameSection,
    setSectionColumns,
    isLayoutReady: layout !== null
  };
}